import {
  useContext, useEffect, useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useSelector, useDispatch } from 'react-redux';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import AutoAwesomeTwoToneIcon from '@mui/icons-material/AutoAwesomeTwoTone';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Divider from '@mui/material/Divider';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import PrintIcon from '@mui/icons-material/Print';
import ButtonGroup from '@mui/material/ButtonGroup';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Alert from '@mui/material/Alert';

import {
  clearDraft,
} from '#features/cart/draft.slice';
import {
  validate,
  buildAllTouched,
  additionalChargesValidations,
  additionalChargesKeys,
  paymentInfoValidations,
  paymentInfoKeys,
} from '#common/validation';
import setDiffsHelper from '#utils/set-diffs-helper';
import makeid from '#utils/make-id';
import { removeDraftDiff, type Diff } from '#utils/order-diff-utils';
import {
  formatDate,
  formatCurrency,
  calcOrderTotalPrice,
  calcMinWearDate,
  isCustomizedItem,
  calcInvoiceTotal,
  calcTransactionTotal,
  formatTrackingNumber,
} from '#common/utils';
import {
  formatTransactionItems,
  formatChargeInfo,
} from '#common/transaction-utils';
import URLSearchParamsEx from '#utils/url-ex';
import useUnsavedChangesWarning from '#hooks/useUnsavedChangesWarning';
import {
  INOVICE_ITEM_TYPES,
  FULFILLMENT_DATA,
  FULFILLMENT_STATUS,
  FULFILLMENT_LITERAL,
} from '#common/constants';
import {
  ADDONS,
} from '#common/constants/addons';
import {
  useGetOrderMutation,
  useUpdateOrderMutation,
  useUpdatePendingItemMutation,
} from '#services/order';
import ChipInput from '#components/ChipInput';
import EnhancedTable, { EnhancedTableHeader } from '#components/EnhancedTable';
import OrderItemDetails, {
  OrderItemDetailsItem,
  OrderItemDetailsOrder,
} from '#components/OrderItemDetails';
import { FeedbackUIContext } from '#context/feedback-ui-context';
import TitleBar, { TitleBarItemTraveler } from '#components/TitleBar';
import ShippingInfoPanel from './ShippingInfoPanel';
import InvoicePanel from './InvoicePanel';
import TransactionPanel from './TransactionPanel';
import RemindersPanel from './RemindersPanel';
import PendingItemStatus, { type PendingItemUpdate } from './PendingItemStatus';

interface OrderUser {
  id: string | number;
  username?: string;
  code?: string | number;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
  notes?: string;
  disableShippingInfo?: boolean;
  shippingName?: string;
  shippingEmail?: string;
  shippingPhone?: string;
  shippingAddress?: string;
  disableBillingInfo?: boolean;
  billingName?: string;
  billingEmail?: string;
  billingPhone?: string;
  billingAddress?: string;
  [key: string]: unknown;
}

interface OrderBarcodeItem {
  barcode: string;
  itemId?: string | number;
  inventoryOrderId?: string | number;
  bulkOrderId?: string | number;
  received?: boolean;
  [key: string]: unknown;
}

interface BarcodeRef {
  barcode: string;
  inventoryOrderId?: string | number;
}

interface BarcodeDisplay {
  barcode: string;
  inventoryOrderId?: string | number | undefined;
  bulkOrderId?: string | number | undefined;
}

interface OrderItem {
  id?: string | number;
  _id?: string;
  hasInventory?: boolean;
  fromInventory?: boolean;
  unallocatedBarcodes?: BarcodeRef[];
  barcodeItems?: BarcodeRef[];
  amounts?: number;
  unitPrice?: number;
  totalPrice?: number;
  isSampleItem?: boolean;
  shipDatePending?: boolean;
  confirmDeadline?: string | null;
  [key: string]: unknown;
}

interface AdditionalCharge {
  _id: string;
  name: string;
  price?: number | string;
  createdDate?: string;
  [key: string]: unknown;
}

interface Pkg {
  _id?: string;
  barcodeItems: string[];
  [key: string]: unknown;
}

interface InvoiceItem {
  type: string;
  value: string;
}

interface Invoice {
  _id?: string;
  number?: string | number;
  total?: number;
  invoiceDate?: string | Date;
  items: InvoiceItem[];
  [key: string]: unknown;
}

interface Transaction {
  _id?: string;
  amount?: number | string;
  items?: Array<Record<string, unknown>>;
  [key: string]: unknown;
}

interface PaymentInfo {
  _id?: string;
  amount?: number | string;
  [key: string]: unknown;
}

interface CouponSnapshot {
  code?: string;
  name?: string;
}

interface CouponChange {
  code: string;
  type: string;
  reason?: string;
  oldAmount?: number;
  newAmount?: number;
}

interface Order {
  id: string | number;
  _id?: string | number;
  orderIndex?: number;
  total?: number;
  relatedSalespersonId?: string | number | null;
  items: OrderItem[];
  cancelledItems?: OrderItem[];
  tags: string[];
  additionalCharges: AdditionalCharge[];
  packages: Pkg[];
  invoices?: Invoice[];
  transactions?: Transaction[];
  paymentInfo: PaymentInfo[];
  fulfillmentStatus: number;
  barcodeItems: OrderBarcodeItem[];
  user: OrderUser;
  creater: { name?: string; [key: string]: unknown };
  createdDate?: string;
  lastUpdatedBy?: string;
  couponSnapshots?: CouponSnapshot[];
  couponDiscount?: number;
  notes?: string;
  notesForAll?: string;
  enableUpdateEmail?: boolean;
  linkedBulkOrderIds?: unknown;
  queuedCreateMailAt?: string | null;
  [key: string]: unknown;
}

type OrderResponse = Order & { couponChanges?: CouponChange[] };

// Loose, all-optional row shapes for EnhancedTable renderers (a required field
// would make EnhancedTableRow un-assignable and TS would reject the renderer).
interface AdditionalChargeRow {
  name?: string;
  createdDate?: string;
  price?: number;
  _id?: string;
  [key: string]: unknown;
}

interface ShippingRow {
  trackingNumber?: string;
  shipper?: string;
  shipDate?: string | Date;
  barcodeItems?: string[];
  _id?: string;
  [key: string]: unknown;
}

interface InvoiceRow {
  number?: string | number;
  items?: InvoiceItem[];
  invoiceDate?: string | Date;
  total?: number;
  _id?: string;
  [key: string]: unknown;
}

interface TransactionRow {
  items?: Array<Record<string, unknown>>;
  transactionDate?: string | Date;
  paymentMethod?: string;
  amount?: number | string;
  notes?: string;
  _id?: string;
  [key: string]: unknown;
}

interface PaymentRow {
  paymentDate?: string | Date;
  amount?: number | string;
  _id?: string;
  [key: string]: unknown;
}

// A draft item produced by a panel's save callback. Carries an `_id` plus an
// open bag of fields; cast to the relevant collection type at write sites.
interface SaveDraft {
  _id?: string;
  [key: string]: unknown;
}

function isDraftObject(o: unknown): boolean {
  const obj = o as { _id?: unknown } | null | undefined;
  if (!obj) {
    return false;
  }

  if (typeof obj._id !== 'string') {
    return false;
  }

  if (obj._id.startsWith('unsaved_')) {
    return true;
  }

  return false;
}

export default function AdminOrderDetail() {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const {
    showBackdrop,
    hideBackdrop,

    showDialog,
    hideDialog,
    setDialogData,

    simpleSnackbar,

    showModal,
    setModalData,
  } = useContext(FeedbackUIContext)!;

  const [order, setOrder] = useState<Order | undefined>();
  const [additionalCharges, setAdditionalCharges] = useState<AdditionalCharge[]>([]);
  const [packages, setPackages] = useState<Pkg[]>([]);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo[]>([]);

  const [errors, setErrors] = useState<{
    additionalCharges?: Record<string, unknown>;
    paymentInfo?: Record<string, unknown>;
  }>({});
  const [couponChanges, setCouponChanges] = useState<CouponChange[]>([]);
  const [getOrder] = useGetOrderMutation();
  const [updateOrder] = useUpdateOrderMutation();
  const [updatePendingItem] = useUpdatePendingItemMutation();
  const [diffs, setDiffs] = useState<Diff[]>([]);

  useUnsavedChangesWarning({
    hasUnsavedChanges: () => diffs.length > 0,
  });
  const [cancelled, setCancelled] = useState(false);
  const appConfig = useSelector(
    (state: { appConfig: { shippingDelayInWeeks?: number; timeZone?: string } }) => state.appConfig,
  );
  const draftItem = useSelector(
    (state: {
      draftItem: { item?: { _id?: string | number; data?: Record<string, unknown> } };
    }) => state.draftItem.item,
  );
  const userInfo = useSelector(
    (state: {
      auth: {
        userInfo?: {
          id?: string | number;
          isSalesperson?: boolean;
          isAdmin?: boolean;
          isStaff?: boolean;
        };
      };
    }) => state.auth.userInfo,
  );

  const urlParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);

  const isDifferentSalesperson = userInfo?.isSalesperson
    && order
    // order created by different assigned salesperson
    && (order.relatedSalespersonId !== userInfo?.id)
    // order is created before the customer assgined to any salesperson
    && !order.relatedSalespersonId;

  useEffect(() => {
    showBackdrop();
    void getOrder(urlParams.values as unknown as Parameters<typeof getOrder>[0])
      .then((res) => {
        const result = res as { error?: unknown; data?: Order };
        if (result.error) {
          console.log(result.error);
          return;
        }

        const data = result.data as Order;
        const newOrder = {
          sampleItemIds: [],
          ...data,
          items: data.items.map((itm) => ({ ...itm })),
        } as Order;
        if (draftItem && data._id === draftItem._id) {
          const newItem = { ...draftItem.data } as Record<string, unknown>;
          // preview only; the server stamps the real ship date on save
          if (isCustomizedItem(newItem, newItem.product as Parameters<typeof isCustomizedItem>[1])) {
            newItem.shipDatePending = true;
          } else {
            newItem.estimateShipDate = calcMinWearDate({
              ...newItem,
              shippingDelayInWeeks: appConfig.shippingDelayInWeeks,
            } as unknown as Parameters<typeof calcMinWearDate>[0]).toISOString();
          }
          newItem._id = `unsaved_${makeid(5)}`;
          setDiffs([...diffs, {
            event: 'add',
            scope: 'items',
            data: newItem,
          }]);

          newOrder.items = [...newOrder.items, newItem];
          dispatch(clearDraft());
        }

        setOrder(newOrder);
        newOrder.tags = [...newOrder.tags];
        setAdditionalCharges([...newOrder.additionalCharges]);
        setPackages(newOrder.packages.map((p) => ({ ...p })));
        if (newOrder.invoices) {
          setInvoices(newOrder.invoices.map((i) => ({
            ...i,
            items: [...i.items],
          })));
        }
        if (newOrder.transactions) {
          setTransactions(newOrder.transactions.map((t) => ({
            ...t,
            items: [...(t.items || [])],
          })));
        }
        setPaymentInfo([...newOrder.paymentInfo]);

        setCancelled(newOrder.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED);
      }).finally(() => {
        hideBackdrop();
      });
  }, [location.key]);

  if (!order) {
    return null;
  }

  const handleAddItems = () => {
    if (diffs.length) {
      setDialogData({
        title: 'Notice',
        text: 'Save order before you add new item',
        agree: 'OK',
        onAgree: () => {
          hideDialog();
        },
      });
      showDialog();
      return;
    }
    const shopParams = new URLSearchParamsEx(location as unknown as Record<string, unknown>);
    shopParams.set('orderId', String(order.id));
    shopParams.set('userId', String(order.user.id));
    void navigate(`/shop?${shopParams.toString()}`);
  };

  const handleSaveOrder = () => {
    showBackdrop();

    const isCancel = diffs.some((d) => d.event === 'set'
      && d.scope === 'fulfillmentStatus'
      && d.data === 3);
    const successText = isCancel
      ? 'Order has been cancelled.'
      : 'Order has been saved.';

    void updateOrder({
      userId: order.user.id,
      orderId: order.id,
      diffs,
    } as unknown as Parameters<typeof updateOrder>[0]).then((res) => {
      const hasError = simpleSnackbar(
        res as unknown as Parameters<typeof simpleSnackbar>[0],
        successText,
      );
      if (hasError) {
        return;
      }

      const result = res as { data?: OrderResponse };
      setCouponChanges(result.data?.couponChanges || []);

      const data = result.data as Order;
      const newOrder = {
        orderIndex: order.orderIndex,
        total: order.total,
        ...data,
        items: data.items.map((itm) => ({ ...itm })),
      } as Order;
      newOrder.tags = [...newOrder.tags];
      setOrder(newOrder);
      setAdditionalCharges([...newOrder.additionalCharges]);
      setPackages(newOrder.packages.map((p) => ({ ...p })));
      if (newOrder.invoices) {
        setInvoices(newOrder.invoices.map((i) => ({
          ...i,
          items: [...i.items],
        })));
      }
      if (newOrder.transactions) {
        setTransactions(newOrder.transactions.map((t) => ({
          ...t,
          items: [...(t.items || [])],
        })));
      }
      setPaymentInfo([...newOrder.paymentInfo]);

      setDiffs([]);
      setCancelled(newOrder.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED);
    }).finally(() => {
      hideBackdrop();
    });
  };

  // Saves right away (outside the diff flow) and merges the saved items back,
  // so other unsaved edits on the page survive.
  const handlePendingItemUpdate = (item: OrderItem, update: PendingItemUpdate) => {
    showBackdrop();
    return updatePendingItem({
      orderId: order.id,
      itemId: item.id,
      ...update,
    } as unknown as Parameters<typeof updatePendingItem>[0]).then((res) => {
      const hasError = simpleSnackbar(
        res as unknown as Parameters<typeof simpleSnackbar>[0],
        update.shipDate ? 'Ship date has been set.' : 'Deadline has been extended.',
      );
      if (hasError) {
        return false;
      }

      const data = (res as { data: Order & { warning?: string | null } }).data;
      setOrder((current) => current && {
        ...current,
        queuedCreateMailAt: data.queuedCreateMailAt ?? null,
        ...(data.lastUpdatedBy ? { lastUpdatedBy: data.lastUpdatedBy } : {}),
        // only the lifecycle fields change; keep unsaved edits on the item
        items: current.items.map((itm) => {
          if (itm.id !== item.id) {
            return itm;
          }
          const saved = data.items.find((d) => d.id === item.id);
          if (!saved) {
            return itm;
          }
          const next: OrderItem = { ...itm };
          ['shipDatePending', 'confirmDeadline', 'reminderSentAt', 'estimateShipDate'].forEach((k) => {
            delete next[k];
            if (saved[k] !== undefined) {
              next[k] = saved[k];
            }
          });
          return next;
        }),
      });
      if (data.warning) {
        setDialogData({
          title: 'Wear date warning',
          text: data.warning,
          agree: 'OK',
          onAgree: () => hideDialog(),
        });
        showDialog();
      }
      return true;
    }).finally(() => {
      hideBackdrop();
    });
  };

  const handleTravelOrder = (direction: number) => {
    // Reset states before navigating
    setInvoices([]);
    setTransactions([]);
    setAdditionalCharges([]);
    setPackages([]);
    setPaymentInfo([]);
    setDiffs([]);

    urlParams.delete('orderId');
    urlParams.set('orderIndex', (val) => String(Number(val) + direction));
    const search = urlParams.toString();
    void navigate({
      pathname: '/management/order/detail',
      search,
    });
  };

  const createDeleteShippingInfoHandler = (shippingInfo: SaveDraft) => {
    return () => {
      const scope = 'packages';
      if (isDraftObject(shippingInfo)) {
        // delete a new data, just remove it from diffs
        const newDiffs = removeDraftDiff(diffs, scope, shippingInfo._id as string);
        setDiffs([...newDiffs]);
      } else {
        // delete a exists data, should send request to server later
        setDiffs([...diffs, {
          event: 'delete',
          scope,
          target: shippingInfo._id,
        }]);
      }

      const newItems = packages.filter((p) => p._id !== shippingInfo._id);
      setPackages(newItems);
    };
  };

  const createEditShippingInfoHandler = (shippingInfo?: Pkg) => {
    return () => {
      setModalData({
        children: (
          <>
            <Typography variant="h6" gutterBottom>
              {shippingInfo ? 'Edit Shipping Info' : 'New Shipping Info'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <ShippingInfoPanel
              packages={packages}
              shippingInfo={shippingInfo}
              barcodeItems={order.barcodeItems}
              userData={order.user}
              onSaveShippingInfo={(value) => {
                const item = value as unknown as SaveDraft;
                const scope = 'packages';
                item._id = item._id || `unsaved_${makeid(5)}`;

                const exists = packages.find((p) => p._id === item._id);
                if (exists) {
                  Object.assign(exists, item);
                }

                let newDiffs = diffs;
                const diff: Diff = {
                  target: item._id,
                  scope,
                  data: exists || item,
                };

                if (isDraftObject(item)) {
                  if (exists) {
                    // diff already exists remove the previous one
                    newDiffs = diffs.filter((d) => d.scope === scope && d.target !== item._id);
                  } else {
                    setPackages([...packages, item as unknown as Pkg]);
                  }
                  diff.event = 'add';
                } else {
                  diff.event = 'set';
                }

                newDiffs.push(diff);
                setDiffs(newDiffs);
              }}
            />
          </>
        ),
        style: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 720,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        },
      });
      showModal();
    };
  };

  const createDeleteInvoiceHandler = (invoice: SaveDraft) => {
    return () => {
      const scope = 'invoices';
      if (isDraftObject(invoice)) {
        // delete a new data, just remove it from diffs
        const newDiffs = removeDraftDiff(diffs, scope, invoice._id as string);
        setDiffs([...newDiffs]);
      } else {
        // delete an exists data, should send request to server later
        setDiffs([...diffs, {
          event: 'delete',
          scope,
          target: invoice._id,
        }]);
      }

      const newItems = invoices.filter((i) => i._id !== invoice._id);
      setInvoices(newItems);
    };
  };

  const createEditInvoiceHandler = (invoice?: Invoice) => {
    return () => {
      setModalData({
        children: (
          <>
            <Typography variant="h6" gutterBottom>
              {invoice ? 'Edit Invoice' : 'New Invoice'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <InvoicePanel
              packages={packages}
              invoice={invoice}
              invoices={invoices}
              // barcodeItems={order.barcodeItems}
              additionalCharges={additionalCharges}
              onSaveInvoice={(value) => {
                const item = value as unknown as SaveDraft;
                const scope = 'invoices';
                item._id = item._id || `unsaved_${makeid(5)}`;

                const exists = invoices.find((p) => p._id === item._id);
                if (exists) {
                  Object.assign(exists, item);
                }

                let newDiffs = diffs;
                const diff: Diff = {
                  target: item._id,
                  scope,
                  data: exists || item,
                };

                if (isDraftObject(item)) {
                  if (exists) {
                    // diff already exists remove the previous one
                    newDiffs = diffs.filter((d) => d.scope === scope && d.target !== item._id);
                  } else {
                    setInvoices([...invoices, item as unknown as Invoice]);
                  }
                  diff.event = 'add';
                } else {
                  diff.event = 'set';
                }

                newDiffs.push(diff);
                setDiffs(newDiffs);
              }}
            />
          </>
        ),
        style: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 720,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        },
      });
      showModal();
    };
  };

  const createDeleteTransactionHandler = (transaction: SaveDraft) => {
    return () => {
      const scope = 'transactions';
      if (isDraftObject(transaction)) {
        // delete a new data, just remove it from diffs
        const newDiffs = removeDraftDiff(diffs, scope, transaction._id as string);
        setDiffs([...newDiffs]);
      } else {
        // delete an exists data, should send request to server later
        setDiffs([...diffs, {
          event: 'delete',
          scope,
          target: transaction._id,
        }]);
      }

      const newItems = transactions.filter((t) => t._id !== transaction._id);
      setTransactions(newItems);
    };
  };

  const createEditTransactionHandler = (transaction?: Transaction) => {
    return () => {
      setModalData({
        children: (
          <>
            <Typography variant="h6" gutterBottom>
              {transaction ? 'Edit Transaction' : 'New Transaction'}
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <TransactionPanel
              transaction={transaction as unknown as Parameters<typeof TransactionPanel>[0]['transaction']}
              transactions={transactions as unknown as Parameters<typeof TransactionPanel>[0]['transactions']}
              order={order as unknown as Parameters<typeof TransactionPanel>[0]['order']}
              additionalCharges={additionalCharges as unknown as Parameters<typeof TransactionPanel>[0]['additionalCharges']}
              onSaveTransaction={(value: unknown) => {
                const item = value as SaveDraft;
                const scope = 'transactions';
                item._id = item._id || `unsaved_${makeid(5)}`;

                const exists = transactions.find((t) => t._id === item._id);
                if (exists) {
                  Object.assign(exists, item);
                }

                let newDiffs = diffs;
                const diff: Diff = {
                  target: item._id,
                  scope,
                  data: exists || item,
                };

                if (isDraftObject(item)) {
                  if (exists) {
                    // diff already exists remove the previous one
                    newDiffs = diffs.filter((d) => d.scope === scope && d.target !== item._id);
                  } else {
                    setTransactions([...transactions, item]);
                  }
                  diff.event = 'add';
                } else {
                  diff.event = 'set';
                }

                newDiffs.push(diff);
                setDiffs(newDiffs);
              }}
            />
          </>
        ),
        style: {
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 800,
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
        },
      });
      showModal();
    };
  };

  function createDownloadInvoiceHandler(invoice: InvoiceRow) {
    return () => {
      const url = `/api/downloadInvoice?invoiceNumber=${invoice.number}`;
      window.open(url);
    };
  }

  // order will be locked after be added to a bulk order
  const locked = !!order.linkedBulkOrderIds;

  let tableHeaders: EnhancedTableHeader[] = [{
    id: 'id',
    label: 'Item',
    renderer: (item: OrderItem) => (
      <>
        <OrderItemDetails
          item={item as unknown as OrderItemDetailsItem}
          order={order as unknown as OrderItemDetailsOrder}
          cancelled={cancelled}
        />
        {item.shipDatePending && !cancelled && !isDraftObject(item) && (
          <PendingItemStatus
            confirmDeadline={item.confirmDeadline}
            orderCreatedDate={order.createdDate}
            canEdit={!!(userInfo?.isAdmin || userInfo?.isStaff)}
            onSave={(update) => handlePendingItemUpdate(item, update)}
          />
        )}
        {/* add a new condition to check if the item has inventory */}
        {item.hasInventory
         && !item.fromInventory
         && (!order.barcodeItems.some((bi) => bi.itemId === item.id)) && (
          <>
            <Typography
              sx={{
                color: 'warning.main',
                mt: 1,
                fontSize: '0.875rem',
                fontStyle: 'italic',
                displayPrint: 'none',
                whiteSpace: 'normal',
              }}
            >
              {item.unallocatedBarcodes?.length}
              {' '}
              available inventories found:
              {(item.unallocatedBarcodes || []).map(({ barcode, inventoryOrderId }) => (
                <Link
                  key={barcode}
                  href={`/management/inventory/detail?inventoryOrderId=${inventoryOrderId}`}
                  onClick={(e) => {
                    e.preventDefault();
                    void navigate(`/management/inventory/detail?inventoryOrderId=${inventoryOrderId}`);
                  }}
                  sx={{
                    ml: 0.5,
                    textDecoration: 'inherit',
                  }}
                >
                  {barcode}
                </Link>
              ))}
              <Tooltip title="Experimental">
                <AutoAwesomeTwoToneIcon />
              </Tooltip>
            </Typography>
          </>
        )}
      </>
    ),
  }, {
    id: 'barcodes',
    label: 'Barcodes',
    renderer: (item: OrderItem) => {
      let barcodes: BarcodeDisplay[] = [];
      if (item.fromInventory) {
        barcodes = item.barcodeItems && item.barcodeItems.length
          ? item.barcodeItems.map((bi) => ({
            barcode: bi.barcode,
            inventoryOrderId: bi.inventoryOrderId,
          }))
          : [];
      } else {
        order.barcodeItems.forEach((bi) => {
          if (bi.itemId === item.id) {
            barcodes.push({
              barcode: bi.barcode,
              inventoryOrderId: bi.inventoryOrderId,
              bulkOrderId: bi.bulkOrderId,
            });
          }
        });
      }
      return barcodes.map(({ barcode, inventoryOrderId, bulkOrderId }, idx) => {
        if (inventoryOrderId) {
          return (
            <div key={idx}>
              <Link
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => { void navigate(`/management/inventory/detail?inventoryOrderId=${inventoryOrderId}`); }}
              >
                {barcode}
              </Link>
            </div>
          );
        }
        if (bulkOrderId) {
          return (
            <div key={idx}>
              <Link
                sx={{
                  cursor: 'pointer',
                }}
                onClick={() => { void navigate(`/management/bulk/detail?bulkOrderId=${bulkOrderId}`); }}
              >
                {barcode}
              </Link>
            </div>
          );
        }
        return (
          <div key={idx}>
            {barcode}
          </div>
        );
      });
    },
  }, {
    id: 'amounts',
    label: 'QTY',
    renderer: (item: OrderItem) => (cancelled ? 0 : item.amounts),
  }, {
    id: 'unitPrice',
    label: 'Unit Price',
    renderer: (item: OrderItem) => (cancelled
      ? `${formatCurrency(0)}`
      : `${formatCurrency(item.unitPrice as unknown as Parameters<typeof formatCurrency>[0])}`),
  }, {
    id: 'totalPrice',
    label: 'Total',
    renderer: (item: OrderItem) => (cancelled
      ? `${formatCurrency(0)}`
      : `${formatCurrency(item.totalPrice as unknown as Parameters<typeof formatCurrency>[0])}`),
  }, {
    id: 'status',
    label: 'Status',
    renderer: (item: OrderItem) => {
      if (cancelled) {
        return FULFILLMENT_LITERAL[order.fulfillmentStatus];
      }

      const itemBarcodes = item.barcodeItems && item.barcodeItems.length
        ? item.barcodeItems.map((bi) => bi.barcode)
        : [];

      const barcodes = order.barcodeItems.filter((bi) => {
        if (bi.itemId === item.id) {
          // normal order items
          return true;
        }

        if (itemBarcodes && itemBarcodes.includes(bi.barcode)) {
          // inventory items
          return true;
        }

        return false;
      });

      if (!barcodes.length) {
        return <div>Order Confirmed</div>;
      }

      const received = barcodes.filter((b) => b.received);
      const shipped = received.filter((rc) => {
        return packages.some((p) => p.barcodeItems.includes(rc.barcode));
      });
      return (
        <>
          <div>
            {`In Warehouse (${received.length} of ${barcodes.length})`}
          </div>
          <div>
            {`Shipped (${shipped.length} of ${barcodes.length})`}
          </div>
        </>
      );
    },
  }];

  if (!isDifferentSalesperson) {
    tableHeaders.push({
      id: 'action',
      label: 'Action',
      hideForPrint: true,
      renderer: (item: OrderItem) => {
        return (
          <Link
            key="btn-delete"
            component="button"
            onClick={() => {
              const scope = 'items';
              if (isDraftObject(item)) {
                // delete a new data, just remove it from diffs
                const newDiffs = removeDraftDiff(diffs, scope, item._id as string);
                setDiffs([...newDiffs]);
              } else {
                // delete a exists data, should send request to server later
                setDiffs([...diffs, {
                  event: 'delete',
                  scope,
                  target: item._id,
                }]);
              }

              const newItems = order.items.filter((itm) => itm._id !== item._id);
              order.items = newItems;
              setOrder(order);
            }}
          >
            Delete
          </Link>
        );
      },
    });
  }

  if (window.subscriptions.available[ADDONS.SALESPERSON]
    && !isDifferentSalesperson) {
    tableHeaders.push({
      id: 'sample',
      label: 'Sample Item?',
      renderer: (item: OrderItem) => {
        return (
          <FormControlLabel
            sx={{
              mx: 1,
            }}
            label=""
            control={(
              <Switch
                checked={!!item.isSampleItem}
                disabled={(item.amounts ?? 0) > 1}
                onChange={(e) => {
                  if (isDraftObject(item)) {
                    const scope = 'items';
                    const current = diffs.find(
                      (d) => d.scope === scope
                        && (d.data as { _id?: string } | undefined)?._id === item._id,
                    );
                    (current!.data as { isSampleItem?: boolean }).isSampleItem = e.target.checked;
                    setDiffs([...diffs]);
                  } else {
                    const itemScope = `items.isSampleItem#${item.id}`;
                    setDiffsHelper(itemScope, e.target.checked, diffs, setDiffs);
                    item.isSampleItem = e.target.checked;
                    setOrder({
                      ...order,
                      isSampleItem: e.target.checked,
                    });
                  }
                }}
                name="sample"
                color="primary"
              />
            )}
          />
        );
      },
    });
  }

  let shippingInfoHeaders: EnhancedTableHeader[] = [{
    id: 'trackingNumber',
    label: 'Tracking Number',
    editable: false,
    renderer: (n: ShippingRow) => {
      const trackingLink = formatTrackingNumber(
        n.trackingNumber as unknown as Parameters<typeof formatTrackingNumber>[0],
      );
      return <a href={trackingLink.link as string} target="_blank" rel="noreferrer">{n.trackingNumber}</a>;
    },
  }, {
    id: 'shipper',
    label: 'Shipper',
    editable: false,
  }, {
    id: 'shipDate',
    label: 'Ship Date',
    editable: false,
    // type: 'date',
    renderer: (o: ShippingRow) => formatDate(o.shipDate as unknown as Parameters<typeof formatDate>[0]),
  }, {
    id: 'barcodeItems',
    label: 'Items',
    editable: false,
    renderer: (o: ShippingRow) => (o.barcodeItems || []).map((bi) => <div key={bi}>{bi}</div>),
  }, {
    id: 'actions',
    label: 'Actions',
    hideForPrint: true,
    renderer: (o: ShippingRow) => {
      if (!o) {
        return (
          <Link
            key="btn-add"
            component="button"
            onClick={createEditShippingInfoHandler()}
          >
            Add
          </Link>
        );
      }

      return (
        <>
          <Link
            key="btn-edit"
            component="button"
            onClick={createEditShippingInfoHandler(o as unknown as Pkg)}
          >
            Edit
          </Link>
          <span> / </span>
          <Link
            key="btn-delete"
            component="button"
            onClick={createDeleteShippingInfoHandler(o)}
          >
            Delete
          </Link>
        </>
      );
    },
  }];

  const invoicesHeaders: EnhancedTableHeader[] = [{
    id: 'id',
    label: 'Invoice Number',
    editable: false,
    renderer: (invoice: InvoiceRow) => (invoice.number || '-'),
  }, {
    id: 'items',
    label: 'Items',
    editable: false,
    renderer: (invoice: InvoiceRow) => (invoice.items || [])
      .sort(({ type }) => {
        if (type === INOVICE_ITEM_TYPES.BARCODE_ITEM) {
          return -1;
        }
        return 1;
      })
      .map(({ type, value }) => {
        let text = '';
        if (type === INOVICE_ITEM_TYPES.BARCODE_ITEM) {
          text = value;
        } else if (type === INOVICE_ITEM_TYPES.ADDITIONAL_CHARGE) {
          const find = additionalCharges.find((ac) => ac._id === value);
          text = find ? find.name : '';
        }

        return (<div key={value}>{text}</div>);
      }),
  }, {
    id: 'qty',
    label: 'QTY',
    editable: false,
    renderer: (invoice: InvoiceRow) => (invoice.items || [])
      .length,
  }, {
    id: 'invoiceDate',
    label: 'Created Date',
    editable: false,
    renderer: (invoice: InvoiceRow) => formatDate(
      invoice.invoiceDate as unknown as Parameters<typeof formatDate>[0],
    ),
  }, {
    id: 'total',
    label: 'Total',
    editable: false,
    renderer: (invoice: InvoiceRow) => {
      const total = invoice.total ?? calcInvoiceTotal(
        invoice as unknown as Parameters<typeof calcInvoiceTotal>[0],
        order as unknown as Parameters<typeof calcInvoiceTotal>[1],
        additionalCharges as unknown as Parameters<typeof calcInvoiceTotal>[2],
      );
      return formatCurrency(total);
    },
  }, {
    id: 'actions',
    label: 'Actions',
    hideForPrint: true,
    renderer: (o: InvoiceRow) => {
      const isAdminOrStaff = (userInfo?.isAdmin || userInfo?.isStaff);

      if (!o && isAdminOrStaff) {
        return (
          <Link
            key="btn-add"
            component="button"
            onClick={createEditInvoiceHandler()}
          >
            Add
          </Link>
        );
      }

      if (!o) {
        return null;
      }

      if (isDraftObject(o) && isAdminOrStaff) {
        return (
          <>
            <Link
              key="btn-edit"
              component="button"
              onClick={createEditInvoiceHandler(o as unknown as Invoice)}
            >
              Edit
            </Link>
            <span> / </span>
            <Link
              key="btn-delete"
              component="button"
              onClick={createDeleteInvoiceHandler(o)}
            >
              Delete
            </Link>
          </>
        );
      }

      return (
        <>
          <Link
            key="btn-edit"
            component="button"
            onClick={createDownloadInvoiceHandler(o)}
          >
            Download
          </Link>
          {
            isAdminOrStaff && (
              <>
                <span> / </span>
                <Link
                  key="btn-delete"
                  component="button"
                  onClick={createDeleteInvoiceHandler(o)}
                >
                  Delete
                </Link>
              </>
            )
          }
        </>
      );
    },
  }];

  const transactionsHeaders: EnhancedTableHeader[] = [{
    id: 'id',
    label: 'Transaction #',
    editable: false,
    renderer: (transaction: TransactionRow, idx: number) => (idx + 1),
  }, {
    id: 'items',
    label: 'Items',
    editable: false,
    renderer: (transaction: TransactionRow) => {
      const items = formatTransactionItems(
        transaction.items as unknown as Parameters<typeof formatTransactionItems>[0],
        order as unknown as Parameters<typeof formatTransactionItems>[1],
      );
      if (items.length === 0) {
        return '-';
      }
      return items.map((text, idx) => (<div key={idx}>{text}</div>));
    },
  }, {
    id: 'qty',
    label: 'Item Count',
    editable: false,
    renderer: (transaction: TransactionRow) => (transaction.items || []).length,
  }, {
    id: 'transactionDate',
    label: 'Transaction Date',
    editable: false,
    renderer: (transaction: TransactionRow) => formatDate(
      transaction.transactionDate as unknown as Parameters<typeof formatDate>[0],
    ),
  }, {
    id: 'paymentMethod',
    label: 'Payment Method',
    editable: false,
    renderer: (transaction: TransactionRow) => transaction.paymentMethod || '-',
  }, {
    id: 'amount',
    label: 'Amount',
    editable: false,
    renderer: (transaction: TransactionRow) => {
      const total = transaction.amount || calcTransactionTotal(
        transaction as unknown as Parameters<typeof calcTransactionTotal>[0],
        order as unknown as Parameters<typeof calcTransactionTotal>[1],
        additionalCharges as unknown as Parameters<typeof calcTransactionTotal>[2],
      );
      const chargeInfo = formatChargeInfo(
        transaction as unknown as Parameters<typeof formatChargeInfo>[0],
      );
      return `${formatCurrency(total as unknown as Parameters<typeof formatCurrency>[0])}${chargeInfo}`;
    },
  }, {
    id: 'notes',
    label: 'Notes',
    editable: false,
    renderer: (transaction: TransactionRow) => transaction.notes || '-',
  }, {
    id: 'actions',
    label: 'Actions',
    hideForPrint: true,
    renderer: (o: TransactionRow) => {
      const isAdminOrStaff = (userInfo?.isAdmin || userInfo?.isStaff);

      if (!o && isAdminOrStaff) {
        return (
          <Link
            key="btn-add"
            component="button"
            onClick={createEditTransactionHandler()}
          >
            Add
          </Link>
        );
      }

      if (!o) {
        return null;
      }

      if (isDraftObject(o) && isAdminOrStaff) {
        return (
          <>
            <Link
              key="btn-edit"
              component="button"
              onClick={createEditTransactionHandler(o)}
            >
              Edit
            </Link>
            <span> / </span>
            <Link
              key="btn-delete"
              component="button"
              onClick={createDeleteTransactionHandler(o)}
            >
              Delete
            </Link>
          </>
        );
      }

      return (
        <>
          {
            isAdminOrStaff && (
              <Link
                key="btn-delete"
                component="button"
                onClick={createDeleteTransactionHandler(o)}
              >
                Delete
              </Link>
            )
          }
        </>
      );
    },
  }];

  if (locked && !cancelled) {
    tableHeaders = tableHeaders.filter((th) => th.id !== 'action');
  } else if (locked || cancelled) {
    tableHeaders = tableHeaders.filter((th) => th.id !== 'action');
    shippingInfoHeaders = shippingInfoHeaders.filter((th) => th.id !== 'action');
  }

  const orderProps = {
    headers: tableHeaders,
    rows: order.items,
  };

  const additionalChargesProps = {
    rows: additionalCharges,
  };

  const shippingInfoProps = {
    rows: packages,
  };

  const transactionsProps = {
    rows: transactions,
  };

  const invoicesProps = {
    rows: invoices,
  };

  const paymentInfoProps = {
    rows: paymentInfo,
  };

  const subtotal = cancelled
    ? 0
    : calcOrderTotalPrice({
      ...order,
      additionalCharges,
      couponDiscount: 0, // Don't include coupon discount in subtotal display
    } as unknown as Parameters<typeof calcOrderTotalPrice>[0]);

  // Calculate total after discount
  const totalAfterDiscount = order.couponDiscount
    ? subtotal - order.couponDiscount
    : subtotal;

  const renderActionButton = () => {
    if (cancelled) {
      return null;
    }
    const isCancel = (order.fulfillmentStatus === FULFILLMENT_STATUS.CANCELED);

    return (
      <Button
        variant="contained"
        color={isCancel ? 'error' : 'primary'}
        onClick={handleSaveOrder}
        disabled={!diffs.length}
      >
        {
          isCancel
            ? 'Cancel Order'
            : 'Save Order'
        }
      </Button>
    );
  };

  return (
    <>
      <TitleBar
        title="Customer Order Detail"
        hideActionButtons={!urlParams.values.orderIndex
          // If the filter is barcode and the filter is notempty, hide the action buttons as there is only one result
          || (urlParams.values.filterKey === 'barcode' && (urlParams.values.filter as string).length !== 0)}
      >
        <TitleBarItemTraveler
          onTravelItem={(direction) => handleTravelOrder(direction)}
          itemIndex={order.orderIndex as number}
          itemSize={order.total as number}
        />
      </TitleBar>
      {!!order.queuedCreateMailAt
        && order.items.some((itm) => itm.shipDatePending)
        && (userInfo?.isAdmin || userInfo?.isStaff) && (
        <Alert severity="info" sx={{ mb: 2, displayPrint: 'none' }}>
          {`Confirmation email sends at ${new Intl.DateTimeFormat('en-US', {
            timeStyle: 'short',
            ...(appConfig.timeZone ? { timeZone: appConfig.timeZone } : {}),
          }).format(new Date(order.queuedCreateMailAt))} — set ship dates before then to include them.`}
        </Alert>
      )}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          <Link
            component="button"
            variant="h6"
            sx={{ fontWeight: 'bold', verticalAlign: 'baseline' }}
            onClick={() => { void navigate(`/management/user/detail?userId=${order.user.id}`); }}
          >
            {order.user.username}
            {' '}
            #
            {order.user.code}
          </Link>
        </Typography>
      </Box>
      <RemindersPanel orderId={order.id} />
      <Box sx={{
        my: 1,
        display: 'flex',
        justifyContent: 'space-between',
      }}
      >
        <Box sx={{ typography: 'h7' }}>
          Order #
          {order.id}
          <Button
            sx={{
              ml: 1,
              displayPrint: 'none',
            }}
            variant="outlined"
            onClick={handleAddItems}
            disabled={locked || cancelled || isDifferentSalesperson}
          >
            Add Items
          </Button>
        </Box>

        <ButtonGroup
          sx={{
            displayPrint: 'none',
          }}
          variant="outlined"
          size="small"
        >
          <Button
            variant="text"
            startIcon={<PrintIcon />}
            onClick={() => window.print()}
          >
            Print
          </Button>

          <FormControl variant="standard" sx={{ m: 1, minWidth: 120 }}>
            <Select
              size="small"
              value={order.fulfillmentStatus}
              disabled={cancelled}
              onChange={(e) => {
                const newValue = e.target.value;
                setDiffsHelper('fulfillmentStatus', newValue, diffs, setDiffs);
                setOrder({
                  ...order,
                  fulfillmentStatus: newValue,
                });
              }}
            >
              {
                FULFILLMENT_DATA
                  .filter((fd) => !(packages.length && fd.id === FULFILLMENT_STATUS.CANCELED))
                  .map((fst) => (
                    <MenuItem key={fst.id} value={fst.id}>{fst.text}</MenuItem>
                  ))
              }
            </Select>
          </FormControl>
        </ButtonGroup>
      </Box>
      <EnhancedTable
        name="order-detail-admin"
        {...orderProps}
      />
      {!!order.cancelledItems?.length && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2">
            Cancelled Items
          </Typography>
          <EnhancedTable
            name="order-detail-cancelled"
            rows={order.cancelledItems}
            headers={[{
              id: '',
              label: 'Item',
              renderer: (item: OrderItem) => (
                <OrderItemDetails
                  item={item as unknown as OrderItemDetailsItem}
                  order={order as unknown as OrderItemDetailsOrder}
                  cancelled
                />
              ),
            }, {
              id: 'amounts',
              label: 'QTY',
            }, {
              id: 'unitPrice',
              label: 'Unit Price',
              renderer: (item: OrderItem) => formatCurrency(item.unitPrice as number),
            }, {
              id: 'totalPrice',
              label: 'Total',
              renderer: (item: OrderItem) => formatCurrency(item.totalPrice as number),
            }]}
          />
        </Box>
      )}
      <Box>
        <Typography variant="subtitle2">
          Additional Charge
        </Typography>
        <EnhancedTable
          enableAddNew={!cancelled && !isDifferentSalesperson}
          enableDelete={(data) => (
            !invoices.some((inv) => (
              inv.items.some((itm) => (
                itm.value === data._id))
            )))}
          name="additionalCharges"
          headers={[{
            id: 'name',
            label: 'Additional Charges',
          }, {
            id: 'createdDate',
            label: 'Created Date',
            editable: false,
            renderer: (row: AdditionalChargeRow) => (row.createdDate
              ? formatDate(row.createdDate)
              : 'N/A'),
          }, {
            id: 'price',
            label: 'Amount',
            type: 'number',
            renderer: (row: AdditionalChargeRow) => formatCurrency(
              row.price as unknown as Parameters<typeof formatCurrency>[0],
            ),
            defaultValue: '',
          }]}
          addNewErrors={errors.additionalCharges}
          onDelete={(event, data) => {
            const removed = additionalCharges.splice(data.rowIndex, 1)[0]!;
            if (isDraftObject(removed)) {
              // delete a new data, just remove it from diffs
              const newDiffs = removeDraftDiff(diffs, 'additionalCharges', removed._id);
              setDiffs([...newDiffs]);
            } else {
              // delete a exists data, should send request to server later
              setDiffs([...diffs, {
                event: 'delete',
                scope: 'additionalCharges',
                target: removed._id,
              }]);
            }

            setAdditionalCharges([...additionalCharges]);
          }}
          onAddNew={(event, addNewState) => {
            const newData: Record<string, unknown> = {
              ...addNewState,
              createdDate: new Date().toISOString(),
            };

            newData._id = `unsaved_${makeid(5)}`;
            const touched = buildAllTouched(additionalChargesKeys);
            const result = validate(additionalChargesValidations, newData, touched);

            setErrors({
              ...errors,
              additionalCharges: result.errors,
            });

            if (!result.isValid) {
              return;
            }

            setDiffs([...diffs, {
              event: 'add',
              scope: 'additionalCharges',
              data: newData,
            }]);
            setAdditionalCharges([...additionalCharges, newData as unknown as AdditionalCharge]);
          }}
          {...additionalChargesProps}
        />
      </Box>
      <Box
        sx={{
          typography: 'subtitle2',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <b>Subtotal</b>
        :
        {' '}
        <span data-testid="subtotal">{formatCurrency(subtotal)}</span>
      </Box>
      {/* Display coupon discounts if any */}
      {order.couponSnapshots && order.couponSnapshots.length > 0 && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            bgcolor: 'success.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'success.main',
          }}
        >
          <Typography
            variant="subtitle2"
            gutterBottom
            sx={{
              color: 'success.dark',
            }}
          >
            Coupons Applied:
          </Typography>
          {order.couponSnapshots.map((coupon, index) => (
            <Box
              key={index}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                typography: 'body2',
                color: 'success.dark',
                mb: 0.5,
              }}
            >
              <span>
                <b>{coupon.code}</b>
                {' - '}
                {coupon.name}
              </span>
            </Box>
          ))}
          {order.couponDiscount && order.couponDiscount > 0 && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                typography: 'subtitle2',
                color: 'success.main',
                mt: 1,
                pt: 1,
                borderTop: '1px solid',
                borderColor: 'success.light',
              }}
            >
              <b>Total Discount:</b>
              {' '}
              <span>
                -
                {formatCurrency(order.couponDiscount)}
              </span>
            </Box>
          )}
        </Box>
      )}
      {/* Final Order Total */}
      <Box
        sx={{
          typography: 'h6',
          display: 'flex',
          justifyContent: 'flex-end',
          mt: 2,
          pt: 2,
          borderTop: '2px solid',
          borderColor: 'divider',
        }}
      >
        <b>Order Total</b>
        :
        {' '}
        <span data-testid="order-total">{formatCurrency(totalAfterDiscount)}</span>
      </Box>
      <Divider />
      <Box
        sx={{
          mt: 5,
        }}
      >
        <Typography variant="subtitle2">
          Shipping Info
        </Typography>
        <EnhancedTable
          name="shippingInfo"
          enableAddNew={!cancelled && !!order.barcodeItems.length}
          headers={shippingInfoHeaders}
          {...shippingInfoProps}
        />
      </Box>
      <Box
        sx={{
          mt: 5,
        }}
      >
        <Typography variant="subtitle2">
          Transactions
        </Typography>
        <EnhancedTable
          name="transactions"
          enableAddNew={!cancelled}
          headers={transactionsHeaders}
          {...transactionsProps}
        />
        <Typography variant="subtitle2" sx={{ mt: 2, textAlign: 'right' }}>
          Total Paid via Transactions:
          {' '}
          {formatCurrency(transactions.reduce((acc, trans) => acc + Number(trans.amount || 0), 0))}
        </Typography>
      </Box>
      <Box
        sx={{
          mt: 5,
        }}
      >
        <Typography variant="subtitle2">
          Invoices
        </Typography>
        <EnhancedTable
          name="invoices"
          enableAddNew={!cancelled}
          headers={invoicesHeaders}
          {...invoicesProps}
        />
      </Box>
      <Box>
        <Typography variant="subtitle2">
          Payment Info
        </Typography>
        <EnhancedTable
          enableAddNew={!cancelled}
          name="paymentInfo"
          headers={[{
            id: 'transactionId',
            label: 'Transaction ID',
          },
          {
            id: 'description',
            label: 'Description',
            type: 'multiline',
            sx: {
              maxWidth: 400,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            },
          },
          {
            id: 'paymentDate',
            label: 'Payment Date',
            type: 'date',
            renderer: (row: PaymentRow) => formatDate(
              row.paymentDate as unknown as Parameters<typeof formatDate>[0],
            ),
          },
          {
            id: 'amount',
            label: 'Amount',
            type: 'number',
            defaultValue: '',
            renderer: (row: PaymentRow) => formatCurrency(
              row.amount as unknown as Parameters<typeof formatCurrency>[0],
            ),
          }]}
          addNewErrors={errors.paymentInfo}
          onDelete={(event, data) => {
            const removed = paymentInfo.splice(data.rowIndex, 1)[0]!;
            if (isDraftObject(removed)) {
              // delete a new data, just remove it from diffs
              const newDiffs = removeDraftDiff(diffs, 'paymentInfo', removed._id as string);
              setDiffs([...newDiffs]);
            } else {
              // delete a exists data, should send request to server later
              setDiffs([...diffs, {
                event: 'delete',
                scope: 'paymentInfo',
                target: removed._id,
              }]);
            }

            setPaymentInfo([...paymentInfo]);
          }}
          onAddNew={(event, addNewState) => {
            const newData: Record<string, unknown> = { ...addNewState };

            newData._id = `unsaved_${makeid(5)}`;
            const touched = buildAllTouched(paymentInfoKeys);
            const result = validate(paymentInfoValidations, newData, touched);

            setErrors({
              ...errors,
              paymentInfo: result.errors,
            });

            if (!result.isValid) {
              return;
            }

            setDiffs([...diffs, {
              event: 'add',
              scope: 'paymentInfo',
              data: newData,
            }]);
            setPaymentInfo([...paymentInfo, newData]);
          }}
          {...paymentInfoProps}
        />
        <Typography variant="subtitle2" sx={{ mt: 2, textAlign: 'right' }}>
          Remaining Balance:
          {' '}
          {formatCurrency(totalAfterDiscount - (order.paymentInfo || []).reduce((acc, curr) => acc + Number(curr.amount), 0))}
        </Typography>
      </Box>
      <Paper
        sx={{
          px: 2,
          mt: 2,
          '& .MuiGrid-root': { mb: 2 },
        }}
      >
        <Box sx={{ typography: 'subtitle2' }}>
          <Grid container>
            <Grid size={4}>
              Order Date
            </Grid>
            <Grid size={8}>
              {formatDate(order.createdDate as unknown as Parameters<typeof formatDate>[0])}
            </Grid>

            <Grid size={4}>
              Placed By
            </Grid>
            <Grid size={8}>
              {order.creater.name}
            </Grid>

            <Grid size={4}>
              Last Updated By
            </Grid>
            <Grid size={8}>
              {order.lastUpdatedBy || '-'}
            </Grid>

            <Grid size={4}>
              Store Name
            </Grid>
            <Grid size={8}>
              <Link
                component="button"
                onClick={() => {
                  if (diffs.length) {
                    setDialogData({
                      title: 'Notice',
                      text: 'Please save this order first',
                      agree: 'OK',
                      onAgree: () => {
                        hideDialog();
                      },
                    });
                    showDialog();
                    return;
                  }
                  void navigate(`/management/user/detail?userId=${order.user.id}`);
                }}
              >
                {order.user.username}
                #
                {order.user.code}
              </Link>
            </Grid>

            <Grid size={4}>
              Customer Notes
            </Grid>
            <Grid size={8}>
              {order.user.notes}
            </Grid>

            <Grid size={4}>
              Contact Info
            </Grid>
            <Grid size={8}>
              <Box>{order.user.name}</Box>
              <Box>{order.user.email}</Box>
              <Box>{order.user.phone}</Box>
              <Box>{order.user.address}</Box>
            </Grid>

            {
              !order.user.disableShippingInfo && (
                <>
                  <Grid size={4}>
                    Shipping Info
                  </Grid>
                  <Grid size={8}>
                    <Box>{order.user.shippingName}</Box>
                    <Box>{order.user.shippingEmail}</Box>
                    <Box>{order.user.shippingPhone}</Box>
                    <Box>{order.user.shippingAddress}</Box>
                  </Grid>
                </>
              )
            }

            {
              !order.user.disableBillingInfo && (
                <>
                  <Grid size={4}>
                    Billing Info
                  </Grid>
                  <Grid size={8}>
                    <Box>{order.user.billingName}</Box>
                    <Box>{order.user.billingEmail}</Box>
                    <Box>{order.user.billingPhone}</Box>
                    <Box>{order.user.billingAddress}</Box>
                  </Grid>
                </>
              )
            }

          </Grid>
        </Box>
      </Paper>
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2">
          Notes for customer
        </Typography>
        <TextField
          multiline
          fullWidth
          rows={5}
          value={order.notesForAll || ''}
          disabled={cancelled}
          onChange={(event) => {
            const newNotes = event.target.value;
            setDiffsHelper('notesForAll', newNotes, diffs, setDiffs);
            setOrder({
              ...order,
              notesForAll: newNotes,
            });
          }}
        />
      </Box>
      <Box
        sx={{
          displayPrint: 'none',
          bgcolor: 'background.paper',
          borderRadius: 1,
        }}
      >
        <Typography variant="subtitle2">
          Notes for admin (won
          {'\''}
          t share with the customer)
        </Typography>
        <TextField
          multiline
          fullWidth
          rows={5}
          value={order.notes || ''}
          disabled={cancelled}
          onChange={(event) => {
            const newNotes = event.target.value;
            setDiffsHelper('notes', newNotes, diffs, setDiffs);
            setOrder({
              ...order,
              notes: newNotes,
            });
          }}
        />
      </Box>
      <ChipInput
        fullWidth
        required
        sx={{ mb: 1, displayPrint: 'none' }}
        name="tags"
        label="Tags"
        disabled={cancelled}
        value={order.tags}
        onChange={(event, data) => {
          const newValue = data.value;
          setDiffsHelper('tags', newValue, diffs, setDiffs);
          setOrder({
            ...order,
            tags: newValue,
          });
        }}
      />
      <FormControlLabel
        sx={{
          displayPrint: 'none',
        }}
        control={<Checkbox />}
        label="Send Update Email"
        disabled={cancelled}
        checked={order.enableUpdateEmail || false}
        onChange={(event, data) => {
          setDiffsHelper('enableUpdateEmail', data, diffs, setDiffs);
          setOrder({
            ...order,
            enableUpdateEmail: data,
          });
        }}
      />
      {couponChanges.length > 0 && (
        <Box sx={{ displayPrint: 'none', mt: 1 }}>
          {couponChanges.map((change) => (
            <Alert
              key={change.code}
              severity={change.type === 'removed' ? 'warning' : 'info'}
              sx={{ mb: 1 }}
              onClose={() => setCouponChanges((prev) => prev.filter((c) => c.code !== change.code))}
            >
              {change.type === 'removed'
                ? `Coupon "${change.code}" was removed (${change.reason})`
                : `Coupon "${change.code}" discount recalculated: $${change.oldAmount?.toFixed(2)} → $${change.newAmount?.toFixed(2)}`}
            </Alert>
          ))}
        </Box>
      )}
      <Box sx={{
        displayPrint: 'none',
      }}
      >
        {renderActionButton()}
      </Box>
    </>
  );
}
